import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { ResponsibilityLevelSchema } from './levels.js';
// Request schemas for the responsibilities feature — a project-owned reference-data
// vocabulary (CONVENTIONS §Entity categories) managed under `reference_data:manage`, and
// visible only when the project's field-force module is on (`has_field_force`, ADR-044).
// A responsibility never moves between projects, so there is no platform surface.
//
// A responsibility = a DEFINITION at a fixed `level` (ADR-026 §C). `level` is chosen at
// create and IMMUTABLE thereafter (absent from the PATCH schema below): it pins the entity
// type every future assignment's polymorphic `scope_id` resolves against, so it cannot
// change under assignments already made. `name` .max(64) matches responsibilities.name
// varchar(64); CI-unique per project (uq_responsibilities_name_ci). `is_required` marks a
// responsibility that must be filled for entities at its level — definitional + editable
// here; its ENFORCEMENT rides the assignment surface (APP-185).
// ── POST /api/projects/:projectId/responsibilities ──────────────────────────
// `is_disabled` defaults false and flips via the lifecycle routes — not a create input.
export const CreateResponsibilityRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(64),
    level: ResponsibilityLevelSchema,
    is_required: z.boolean().default(false),
})
    .strict();
// ── PATCH /api/projects/:projectId/responsibilities/:responsibilityId ────────
// `level` is immutable post-create (see above); `is_disabled` flips via the
// disable/reactivate routes. `name` + `is_required` are the mutable definitional fields.
export const UpdateResponsibilityRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(64),
    is_required: z.boolean(),
})
    .strict();
// ── GET /api/projects/:projectId/responsibilities/export — current-view export ──
// Mirrors the list's URL view-state so the file IS what the user sees (ADR-045).
// `status`/`level` arrive comma-separated (the list filters); `q` is the free-text
// search; `lang` drives header/sheet localization. Gated on `reference_data:manage`.
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(64)).max(50).optional());
export const ExportResponsibilitiesQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    status: csvFilter,
    level: csvFilter,
    q: z.string().trim().max(100).optional(),
})
    .strict();
